/**
 * "Command Center" -- the RDS Migration tab's only layout.
 *
 * The hero-first layout: one big verdict at the top ("USING SUPABASE ONLY" /
 * "USING BOTH · SUPABASE + RDS"), the two systems side by side underneath
 * -- each GREEN when it is serving data right now and RED when it is not,
 * both green when both are -- with their realtime usage, then the table map
 * as a grid of cards that carry the same two-colour verdict per table.
 *
 * Structure:
 *   0. LIVE BAR    -- auto-refresh switch, last update, traffic right now
 *   1. HERO BAND   -- the verdict, the two-system chips, the progress meter
 *   2. TWO SYSTEMS -- SUPABASE | RDS, green/red, with realtime usage
 *   3. PRECONDITIONS
 *   4. SOURCE OF TRUTH + toolbar
 *   5. TABLE MAP   -- one card per table, two-system bar, one button
 *   6. STORAGE     -- one card per bucket
 *   7. DETAIL      -- switches / health / activity, collapsed
 *
 * Colour never stands alone: every green or red element also spells out
 * SUPABASE or RDS and its state (IN USE / OFF / PHI / 2 SCREENS ...).
 */
import React from "react";
import {
  Box, HStack, VStack, Text, Button, SimpleGrid, Progress, Flex, Switch, Badge,
} from "@chakra-ui/react";
import {
  Dot, StatePill, SplitBar, PanelCard, SectionLabel, SystemPair, LiveDot, useNow,
  Kpi, Disclosure, ServingBadge, ForcedBadge, EmptyNote, ON_TONE, OFF_TONE, shortAge,
} from "./rdsUi";
import {
  SourceOfTruthCard, ActionBar, TwoSystemsPanel, PreconditionStrip, Note,
} from "./rdsShared";

/**
 * The live bar: is this view refreshing itself, when did it last change,
 * and how much PHI traffic each system took in the last minute. `live` is
 * the host's state; without it (a static render) the bar still shows the
 * usage and says auto-refresh is off.
 */
function LiveBar({ vm, live }) {
  const now = useNow(1000);
  const on = !!live?.on;
  const updatedSec = live?.updatedAt
    ? Math.max(0, Math.round((now - Date.parse(live.updatedAt)) / 1000))
    : null;
  const u = vm.usage;
  const a = vm.rdsActivity;
  const plus = u.floor ? "+" : "";

  return (
    <HStack
      bg="white"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="xl"
      px={4}
      py={2}
      spacing={4}
      flexWrap="wrap"
      justify="space-between"
      boxShadow="0 1px 2px rgba(16,24,40,0.04)"
      data-testid="rds-live-bar"
    >
      <HStack spacing={3} flexWrap="wrap">
        <LiveDot on={on} />
        <Text
          fontSize="xs"
          fontWeight="bold"
          color={on ? `${ON_TONE}.700` : "gray.500"}
          letterSpacing="0.12em"
        >
          {live?.demo ? "SAMPLE" : on ? "LIVE" : "PAUSED"}
        </Text>
        {live?.setOn && !live.demo && (
          <Switch
            size="sm"
            colorScheme="green"
            isChecked={on}
            onChange={(e) => live.setOn(e.target.checked)}
            aria-label="Auto-refresh"
            data-testid="rds-live-toggle"
          />
        )}
        <Text fontSize="xs" color="gray.500">
          {live?.demo
            ? "sample data, nothing is polled"
            : on
              ? `refreshes every ${Math.round((live?.pollMs || 10000) / 1000)} s`
              : "auto-refresh off"}
          {updatedSec !== null ? ` · updated ${shortAge(updatedSec)}` : ""}
        </Text>
        {live?.error && (
          <StatePill tone="red">last refresh failed · showing the previous state</StatePill>
        )}
        {live?.activityError && !live?.error && (
          <StatePill tone="gray">RDS activity log unavailable</StatePill>
        )}
      </HStack>
      <HStack spacing={2} flexWrap="wrap" data-testid="rds-live-traffic">
        <StatePill tone={u.rds.recent ? ON_TONE : "gray"}>
          RDS · {u.rds.recent}{plus} served · 60 s
        </StatePill>
        <StatePill tone={u.supabase.recent ? ON_TONE : "gray"}>
          SUPABASE · {u.supabase.recent}{plus} landed · 60 s
        </StatePill>
        {a.available && (
          <StatePill tone={a.busy ? ON_TONE : "gray"}>
            RDS LOG · {a.phiEvents} PHI events / {Math.round(a.windowSeconds / 60)} min · {a.viewers} viewer{a.viewers === 1 ? "" : "s"}
          </StatePill>
        )}
      </HStack>
    </HStack>
  );
}

export default function LayoutA({ vm, actions, busy, live }) {
  const s = vm.status;
  const pair = vm.systems;

  return (
    <VStack align="stretch" spacing={5} data-testid="rds-layout-a">
      {/* 0 — LIVE BAR ------------------------------------------------------ */}
      <LiveBar vm={vm} live={live} />

      {/* 1 — HERO BAND ---------------------------------------------------- */}
      <Box
        bg={`${pair.tone}.50`}
        borderWidth="1px"
        borderColor={`${pair.tone}.300`}
        borderRadius="2xl"
        px={{ base: 4, md: 6 }}
        py={5}
        boxShadow="0 1px 3px rgba(16,24,40,0.05)"
        data-testid="rds-headline"
        data-mode={pair.mode}
      >
        <Flex gap={6} align="flex-start" justify="space-between" flexWrap="wrap">
          <Box flex="1" minW="280px">
            <HStack spacing={3} mb={2} flexWrap="wrap">
              <Badge
                colorScheme={pair.tone}
                variant="solid"
                fontSize="11px"
                px={3}
                py="4px"
                borderRadius="md"
                letterSpacing="0.1em"
                data-testid="rds-headline-mode"
              >
                {pair.headline}
              </Badge>
              <HStack spacing={2}>
                <Dot tone={s.tone} size="10px" boxShadow />
                <Text
                  fontSize="10px"
                  fontWeight="bold"
                  color={`${s.tone}.700`}
                  letterSpacing="0.14em"
                  textTransform="uppercase"
                >
                  {s.label}
                </Text>
              </HStack>
            </HStack>
            <Text
              fontSize={{ base: "lg", md: "2xl" }}
              fontWeight="bold"
              color="gray.900"
              lineHeight="1.25"
              maxW="640px"
            >
              {s.title}
            </Text>
            <Text fontSize="sm" color="gray.600" mt={2} maxW="680px">
              {s.body}
            </Text>

            <HStack spacing={2} mt={4} flexWrap="wrap">
              <SystemPair systems={pair} testidPrefix="rds-pair" />
              <StatePill tone={vm.buildOn ? ON_TONE : OFF_TONE}>
                BUILD SWITCH {vm.buildOn ? "ON" : "OFF"}
              </StatePill>
              {vm.localPaused
                ? <StatePill tone={OFF_TONE}>RDS OFF IN THIS NOTESAI</StatePill>
                : vm.paused
                  ? <StatePill tone={OFF_TONE}>RDS PAUSED</StatePill>
                  : vm.killed && <StatePill tone={OFF_TONE}>KILL SWITCH ON</StatePill>}
            </HStack>
          </Box>

          <Box w={{ base: "100%", md: "280px" }} flexShrink={0}>
            <Box
              bg="white"
              borderWidth="1px"
              borderColor="gray.200"
              borderRadius="xl"
              p={4}
            >
              <Text fontSize="10px" fontWeight="bold" color="gray.500" letterSpacing="0.1em" textTransform="uppercase">
                PHI on RDS
              </Text>
              <Text fontSize="3xl" fontWeight="bold" color="gray.900" lineHeight="1.1" mt={1}>
                {vm.progress.onRds}
                <Text as="span" fontSize="lg" color="gray.400" fontWeight="bold">
                  {" "}/ {vm.progress.total} tables
                </Text>
              </Text>
              <Progress
                value={vm.progress.pct}
                colorScheme={vm.progress.onRds > 0 ? ON_TONE : "gray"}
                size="sm"
                borderRadius="full"
                bg="gray.100"
                mt={2}
                data-testid="rds-headline-progress"
              />
              <HStack spacing={3} mt={3}>
                <Text fontSize="xs" color="gray.500">
                  {vm.progress.pct}% moved
                </Text>
                {vm.progress.blocked > 0 && (
                  <Text fontSize="xs" color="orange.600" fontWeight="bold">
                    {vm.progress.blocked} blocked
                  </Text>
                )}
              </HStack>
            </Box>
          </Box>
        </Flex>
      </Box>

      {/* 2 — TWO SYSTEMS --------------------------------------------------- */}
      <Box>
        <TwoSystemsPanel vm={vm} />
      </Box>

      {/* 3 — PRECONDITIONS ------------------------------------------------- */}
      <PreconditionStrip vm={vm} />

      {/* 4 — SOURCE OF TRUTH ----------------------------------------------- */}
      <SourceOfTruthCard vm={vm} actions={actions} busy={busy} />

      {/* 5 — TABLE MAP ----------------------------------------------------- */}
      <Box>
        <SectionLabel
          right={
            <Text fontSize="xs" color="gray.500">
              Turn on = every read of that table takes its PHI from RDS · green = serving, red = not
            </Text>
          }
        >
          Where each PHI table&apos;s data comes from right now
        </SectionLabel>
        {vm.tables.length === 0 ? (
          <PanelCard>
            <EmptyNote>RDS reports no PHI table switches yet.</EmptyNote>
          </PanelCard>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={3}>
            {vm.tables.map((row) => (
              <Box
                key={row.key}
                bg="white"
                borderWidth="1px"
                borderColor={`${row.systems.rds.tone}.200`}
                borderTopWidth="4px"
                borderTopColor={`${row.systems.rds.tone}.400`}
                borderRadius="xl"
                px={4}
                py={3}
                boxShadow="0 1px 2px rgba(16,24,40,0.04)"
                data-testid={`phi-source-${row.table}`}
                data-rds-on={row.systems.rds.on ? "true" : "false"}
              >
                <HStack justify="space-between" align="flex-start" spacing={2}>
                  <HStack spacing={2} flexWrap="wrap" minW={0}>
                    <Text fontSize="sm" fontWeight="bold" color="gray.800" wordBreak="break-all">
                      {row.table}
                    </Text>
                    <ForcedBadge row={row} />
                  </HStack>
                  {row.toggleName && (
                    <Button
                      size="xs"
                      colorScheme={row.enabled ? "red" : "green"}
                      variant={row.enabled ? "outline" : "solid"}
                      onClick={() => actions.toggleFlag(row.toggleName, !row.enabled)}
                      isLoading={busy === row.toggleName}
                      isDisabled={row.toggleDisabled || (!!busy && busy !== row.toggleName)}
                      title={row.toggleTitle}
                      data-testid={`phi-source-toggle-${row.table}`}
                    >
                      {row.toggleLabel}
                    </Button>
                  )}
                </HStack>

                <Box mt={2}>
                  <SplitBar row={row} />
                </Box>

                <HStack spacing={2} mt={2} flexWrap="wrap">
                  <ServingBadge row={row} />
                  {row.screensOnRds > 0 && (
                    <StatePill tone={ON_TONE}>{row.phiLabel}</StatePill>
                  )}
                </HStack>

                <Text fontSize="xs" color="gray.600" mt={2}>
                  {row.detail}
                </Text>
              </Box>
            ))}
          </SimpleGrid>
        )}
      </Box>

      {/* 6 — STORAGE ------------------------------------------------------- */}
      {vm.buckets.length > 0 && (
        <Box>
          <SectionLabel
            right={
              <Text fontSize="xs" color="gray.500">
                Turn on = downloads come from RDS when its copy matches the live file
              </Text>
            }
          >
            Where each storage bucket&apos;s files come from
          </SectionLabel>
          <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={3}>
            {vm.buckets.map((row) => (
              <Box
                key={row.key}
                bg="white"
                borderWidth="1px"
                borderColor={`${row.systems.rds.tone}.200`}
                borderLeftWidth="4px"
                borderLeftColor={`${row.systems.rds.tone}.400`}
                borderRadius="xl"
                px={4}
                py={3}
                data-testid={`storage-source-${row.bucket}`}
                data-rds-on={row.systems.rds.on ? "true" : "false"}
              >
                <HStack justify="space-between" align="flex-start" spacing={2}>
                  <Text fontSize="sm" fontWeight="bold" color="gray.800" wordBreak="break-all">
                    {row.bucket}
                  </Text>
                  <Button
                    size="xs"
                    colorScheme={row.enabled ? "red" : "green"}
                    variant={row.enabled ? "outline" : "solid"}
                    onClick={() => actions.toggleFlag(row.toggleName, !row.enabled)}
                    isLoading={busy === row.toggleName}
                    isDisabled={row.toggleDisabled || (!!busy && busy !== row.toggleName)}
                    title={row.toggleTitle}
                    data-testid={`storage-source-toggle-${row.bucket}`}
                  >
                    {row.toggleLabel}
                  </Button>
                </HStack>
                <Box mt={2}>
                  <SplitBar row={row} />
                </Box>
                <HStack spacing={2} mt={2}>
                  <StatePill tone={row.tone}>{row.badge}</StatePill>
                </HStack>
                <Text fontSize="xs" color="gray.600" mt={2} data-testid={`storage-source-state-${row.bucket}`}>
                  {row.detail}
                </Text>
                {row.session.total > 0 && (
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    this session: {row.session.servedRds} from RDS · {row.session.fellBack} from Supabase
                    {row.session.repaired ? ` · ${row.session.repaired} repaired` : ""}
                  </Text>
                )}
              </Box>
            ))}
          </SimpleGrid>
        </Box>
      )}

      {/* 7 — DETAIL -------------------------------------------------------- */}
      <Disclosure
        title="All switches"
        hint="every flag, per screen and per table, with why it is or is not working"
        badge={`${vm.flags.filter((f) => f.effective).length} effective`}
        tone={vm.killed ? "red" : "blue"}
        testid="rds-section-switches"
      >
        <VStack align="stretch" spacing={1}>
          {vm.flags.map((flag) => (
            <HStack key={flag.name} fontSize="xs" spacing={2} flexWrap="wrap" data-testid={`cutover-flag-${flag.name}`}>
              <Text fontWeight="bold" color="gray.700" minW="180px">
                {flag.name} · {flag.table}
              </Text>
              <StatePill tone={flag.enabled ? "green" : "gray"}>{flag.enabled ? "ENABLED" : "DISABLED"}</StatePill>
              <StatePill tone={flag.effective ? "green" : flag.enabled ? "orange" : "gray"}>
                {flag.effective ? "EFFECTIVE" : "NOT EFFECTIVE"}
              </StatePill>
              {flag.forced && (
                <StatePill tone={flag.forced === "on" ? "purple" : "red"}>
                  {flag.forced === "on" ? "FORCED" : "FORCED OFF"}
                </StatePill>
              )}
              {!flag.effective && flag.reason && (
                <Text color={flag.enabled ? "orange.700" : "gray.500"}>{String(flag.reason)}</Text>
              )}
              {flag.updatedText && <Text color="gray.500">updated {flag.updatedText}</Text>}
              <Button
                size="xs"
                variant="outline"
                colorScheme={flag.enabled ? "red" : "green"}
                onClick={() => actions.toggleFlag(flag.toggleName, !flag.enabled)}
                isLoading={busy === flag.toggleName}
                isDisabled={flag.toggleDisabled || (!!busy && busy !== flag.toggleName)}
                title={flag.toggleTitle}
                data-testid={`cutover-toggle-${flag.name}`}
              >
                {flag.toggleLabel}
              </Button>
            </HStack>
          ))}
          {vm.flags.length === 0 && <EmptyNote>No cutover flags seeded.</EmptyNote>}
        </VStack>
        <Box>
          <Text fontSize="xs" fontWeight="bold" color="gray.700" mb={1}>FLAGS (this Django process)</Text>
          <HStack spacing={2} flexWrap="wrap">
            {Object.entries(vm.raw?.flags || {}).map(([name, on]) => (
              <StatePill key={name} tone={on ? "green" : "gray"}>
                {name}: {on ? "ON" : "OFF"}
              </StatePill>
            ))}
          </HStack>
        </Box>
      </Disclosure>

      <Disclosure
        title="Health checks"
        hint="database, mirror freshness, access mirror, audit pipeline"
        badge={vm.readiness?.status?.toUpperCase() || "NOT CHECKED"}
        tone="blue"
        testid="rds-section-health"
      >
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={3}>
          <Kpi
            label="Database"
            value={vm.readiness?.database?.connected ? `${vm.readiness.database.latency_ms} ms` : "Down"}
            tone={vm.readiness?.database?.connected ? "green" : "red"}
            hint={vm.readiness?.database?.connected ? "connected" : "probe failed"}
          />
          <Kpi
            label="PHI mirror"
            value={vm.mirror.present ? `${vm.mirror.tables} copies` : "n/a"}
            tone={vm.mirror.stale === null ? "gray" : vm.mirror.stale ? "orange" : "green"}
            hint={vm.mirror.ageText}
          />
          <Kpi
            label="Access mirror"
            value={vm.accessMirror.syncedText}
            tone={vm.accessMirror.stale ? "orange" : "green"}
            hint="who may see what"
          />
          <Kpi
            label="Audit pipeline"
            value={vm.audit.lastText}
            tone={vm.audit.stale ? "orange" : "green"}
            hint="last PHI event"
          />
        </SimpleGrid>
        {vm.mirror.present && vm.mirror.failed.length > 0 && (
          <Note tone="red">Mirror failures: {vm.mirror.failed.join(", ")}</Note>
        )}
        {vm.mirror.lastRun && (
          <Text fontSize="xs" color={vm.mirror.lastRun.ok ? "gray.600" : "red.600"}>
            Last mirror run ({String(vm.mirror.lastRun.trigger || "?")}){" "}
            {vm.mirror.lastRun.ok ? "ok" : "FAILED"}
            {vm.mirror.lastRun.finished_at ? ` · ${vm.mirror.lastRun.finished_at}` : ""}
          </Text>
        )}
        <Text fontSize="xs" color="gray.500">
          Checked {vm.checkedText} · RDS version {vm.version}
        </Text>
      </Disclosure>

      <Disclosure
        title="Live read activity"
        hint="what each screen actually got from RDS, and what RDS's own log recorded"
        badge={`${vm.activity.total} reads`}
        tone="purple"
        testid="rds-section-activity"
      >
        {vm.activity.total === 0 ? (
          <EmptyNote>
            Nothing observed yet — this fills in once a screen with a switch on actually runs.
          </EmptyNote>
        ) : (
          <>
            <HStack spacing={3} flexWrap="wrap">
              <StatePill tone="green">{vm.activity.servedRds} served by RDS</StatePill>
              <StatePill tone="orange">{vm.activity.fellBack} fell back to Supabase</StatePill>
              <StatePill tone={vm.usage.rds.window ? ON_TONE : "gray"}>
                RDS · {vm.usage.rds.window}{vm.usage.floor ? "+" : ""} in last {Math.round(vm.usage.windowSeconds / 60)} min
              </StatePill>
              <StatePill tone={vm.usage.supabase.window ? ON_TONE : "gray"}>
                SUPABASE · {vm.usage.supabase.window}{vm.usage.floor ? "+" : ""} in last {Math.round(vm.usage.windowSeconds / 60)} min
              </StatePill>
            </HStack>
            <VStack align="stretch" spacing={1}>
              {vm.activity.perPilot.map((p) => (
                <HStack key={p.pilot} fontSize="xs" spacing={3} flexWrap="wrap">
                  <Text fontWeight="bold" color="gray.700" minW="200px">{p.pilot}</Text>
                  <Text color="gray.600">{p.total} reads</Text>
                  <Text color="green.600">{p.servedRds} from RDS</Text>
                  <Text color="orange.600">{p.fellBack} from Supabase</Text>
                </HStack>
              ))}
            </VStack>
          </>
        )}
        {vm.activity.tablesInUse.length > 0 && (
          <Box>
            <Text fontSize="xs" fontWeight="bold" color="gray.700" mb={1}>
              TABLES &amp; COLUMNS CURRENTLY IN USE
            </Text>
            <VStack align="stretch" spacing={1}>
              {vm.activity.tablesInUse.map((t) => (
                <HStack key={t.table} fontSize="xs" spacing={2} flexWrap="wrap">
                  <Text fontWeight="bold" color="gray.700">{t.table}</Text>
                  <Text color="gray.600">
                    {t.columns.length} column{t.columns.length === 1 ? "" : "s"}: {t.columns.join(", ")}
                  </Text>
                </HStack>
              ))}
            </VStack>
          </Box>
        )}
        {vm.rdsActivity.available && (
          <Box data-testid="rds-activity-tail">
            <Text fontSize="xs" fontWeight="bold" color="gray.700" mb={1}>
              RDS AUDIT LOG · NEWEST EVENTS ({vm.rdsActivity.phiEvents} PHI + {vm.rdsActivity.admin} admin in the last {Math.round(vm.rdsActivity.windowSeconds / 60)} min)
            </Text>
            {vm.rdsActivity.tail.length === 0 ? (
              <EmptyNote>No events in the window.</EmptyNote>
            ) : (
              <VStack align="stretch" spacing={0.5}>
                {vm.rdsActivity.tail.map((e) => (
                  <HStack key={e.id} fontSize="xs" spacing={2} flexWrap="wrap">
                    <Text color="gray.500" minW="70px">{shortAge(e.ageSeconds)}</Text>
                    <StatePill tone={e.family === "read" ? "green" : e.family === "write" ? "blue" : e.family === "miss" ? "red" : "gray"}>
                      {e.family}
                    </StatePill>
                    <Text fontWeight="bold" color="gray.700">
                      {e.kind}:{e.name}{e.schema ? ` (${e.schema})` : ""}
                    </Text>
                    <Text color="gray.500">{e.mode}</Text>
                  </HStack>
                ))}
              </VStack>
            )}
          </Box>
        )}
        <Note tone="gray">
          Screen reports are in-memory on this Django process only — they reset on restart, and are incomplete if the
          backend runs more than one worker. The RDS audit log is RDS&apos;s own record and survives both.
        </Note>
      </Disclosure>

      <ActionBar vm={vm} actions={actions} busy={busy} />
    </VStack>
  );
}
